import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import AuthCard from '@/components/AuthCard'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { authClient } from '@/lib/auth-client'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/forgot-password')({
  head: () => ({
    // noindex, same reasoning as /login and /signup.
    meta: pageMeta({
      title: 'Forgot password · Masklabs',
      description: 'Reset your Masklabs account password.',
      path: '/forgot-password',
      noindex: true,
    }),
    links: pageLinks('/forgot-password'),
  }),
  component: ForgotPasswordPage,
})

type RequestState = 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error'

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  // Set once the form has been submitted at least once. Switches the card to
  // the "check your email" view, which we show regardless of outcome so we
  // never reveal whether the address is a real account.
  const [submitted, setSubmitted] = useState(false)
  const [requestState, setRequestState] = useState<RequestState>('idle')
  const { remaining, start } = useResendCooldown()

  async function submitRequest() {
    setRequestState('sending')
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    })
    setSubmitted(true)
    if (!error) {
      setRequestState('sent')
      start()
      return
    }
    if (error.status === 429) {
      setRequestState('rate-limited')
      return
    }
    // Any other failure (network, etc.) — still don't leak account
    // existence, just let them know something went wrong sending it.
    setRequestState('error')
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    void submitRequest()
  }

  if (submitted) {
    return (
      <AuthCard>
        <CardHeader>
          <CardTitle className="text-2xl">Check your email</CardTitle>
          <CardDescription>
            If an account exists for that address, we sent a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Didn't get it? Check your spam folder, or request a new link below.
          </p>
          {requestState === 'rate-limited' ? (
            <p className="mt-3 text-sm text-destructive">
              You've requested a few links already. Please wait a bit before
              trying again.
            </p>
          ) : null}
          {requestState === 'error' ? (
            <p className="mt-3 text-sm text-destructive">
              Couldn't send the reset link. Please try again in a moment.
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={requestState === 'sending' || remaining > 0}
            onClick={() => void submitRequest()}
          >
            {requestState === 'sending'
              ? 'Sending…'
              : remaining > 0
                ? `Resend in ${remaining}s`
                : 'Resend link'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </CardFooter>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <CardHeader>
        <CardTitle className="text-2xl">Forgot password?</CardTitle>
        <CardDescription>
          Enter your email and we'll send you a link to reset your password.
        </CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </FieldGroup>
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button
            type="submit"
            className="w-full"
            disabled={requestState === 'sending'}
          >
            {requestState === 'sending' ? 'Sending…' : 'Send reset link'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </CardFooter>
      </form>
    </AuthCard>
  )
}
